/**
 * Editor operations for the `videoEmbed` / `fileEmbed` uploading
 * placeholders — the wiki sibling of `file-embed/card-file-embed.ts`.
 *
 * The `wiki` prefix on the exported names is HISTORICAL. The video
 * helpers now serve the card-description and comment surfaces too, which
 * register the same `videoEmbed` node with a card-flavoured NodeView;
 * nothing in here is wiki-specific, and the node is located by its
 * transient placeholder attr rather than by anything about the surface.
 *
 * The OS-file drop flow in `<WikiEmbedOrchestrator>` owns the upload
 * lifecycle (presign → S3 PUT → attach); these helpers own the
 * in-editor node lifecycle:
 *
 *   - `insertWikiVideoPlaceholder` / `insertWikiFilePlaceholder` —
 *     insert an uploading placeholder at the caret and return a
 *     placeholder id (or `null` when the editor isn't mounted /
 *     editable). The file chip is INLINE + atomic, so it's wrapped in
 *     its own paragraph — a bare insert would splice it into the
 *     current paragraph, and the drop contract is one line per file.
 *     `videoEmbed` is a block atom and inserts as-is.
 *   - `resolveWikiEmbedPlaceholder` — swap in the durable attrs (the
 *     `fileId`) once the upload lands.
 *   - `failWikiEmbedPlaceholder` — remove the placeholder if the upload
 *     failed / was canceled. A file chip's now-empty wrapping paragraph
 *     goes with it.
 *
 * Resolve / fail locate the node fresh inside the transaction via
 * `descendants` + the transient `uploadingPlaceholderId` attr (identity,
 * not position — the upload is async and concurrent edits shift
 * positions), and dispatch with `addToHistory: false`: undoing right
 * after a resolve must not restore a skeleton with no upload behind it.
 */

import type { Node as ProseMirrorNode } from '@tiptap/pm/model'
import type { Editor } from '@tiptap/react'

// Re-exported so existing wiki importers keep their import path while the
// card / comment embed paths share the same implementation.
export { collapseNodeSelectionAfter } from './node-selection'
import { collapseNodeSelectionAfter } from './node-selection'

export type WikiEmbedPlaceholderType = 'videoEmbed' | 'fileEmbed'

export interface WikiVideoPlaceholderMeta {
  fileName: string
  mimeType: string
}

export interface WikiFilePlaceholderMeta {
  fileName: string
  sizeBytes: number
  mimeType: string
}

let placeholderCounter = 0

function makePlaceholderId(prefix: string): string {
  placeholderCounter += 1
  return `${prefix}-${Date.now()}-${placeholderCounter}`
}

/**
 * Insert an uploading `videoEmbed` placeholder block at the caret.
 * Returns the placeholder id, or `null` when the editor isn't mounted /
 * editable.
 */
export function insertWikiVideoPlaceholder(
  editor: Editor | null,
  meta: WikiVideoPlaceholderMeta,
): string | null {
  if (!editor || editor.isDestroyed || !editor.isEditable) return null
  const placeholderId = makePlaceholderId('wiki-video')
  editor
    .chain()
    .focus()
    // Guard BEFORE the insert too: if a block atom is node-selected (the
    // user clicked or arrowed onto an image), `insertContent` REPLACES it
    // rather than inserting beside it — and a failed upload then removes
    // the placeholder, losing the original outright.
    .command(({ tr }) => {
      collapseNodeSelectionAfter(tr)
      return true
    })
    .insertContent({
      type: 'videoEmbed',
      attrs: {
        fileId: null,
        mimeType: meta.mimeType,
        caption: '',
        uploadingPlaceholderId: placeholderId,
        uploadingFileName: meta.fileName,
      },
    })
    .command(({ tr }) => {
      collapseNodeSelectionAfter(tr)
      return true
    })
    .run()
  return placeholderId
}

/**
 * Insert an uploading `fileEmbed` placeholder chip at the caret,
 * wrapped in its own paragraph (inline atom — see the module doc).
 * Returns the placeholder id, or `null` when the editor isn't mounted /
 * editable.
 */
export function insertWikiFilePlaceholder(
  editor: Editor | null,
  meta: WikiFilePlaceholderMeta,
): string | null {
  if (!editor || editor.isDestroyed || !editor.isEditable) return null
  const placeholderId = makePlaceholderId('wiki-file')
  editor
    .chain()
    .focus()
    // Guard BEFORE the insert too: if a block atom is node-selected (the
    // user clicked or arrowed onto an image), `insertContent` REPLACES it
    // rather than inserting beside it — and a failed upload then removes
    // the placeholder, losing the original outright.
    .command(({ tr }) => {
      collapseNodeSelectionAfter(tr)
      return true
    })
    .insertContent({
      type: 'paragraph',
      content: [
        {
          type: 'fileEmbed',
          attrs: {
            fileId: null,
            fileName: meta.fileName,
            sizeBytes: meta.sizeBytes,
            mimeType: meta.mimeType,
            uploadingPlaceholderId: placeholderId,
          },
        },
      ],
    })
    .command(({ tr }) => {
      collapseNodeSelectionAfter(tr)
      return true
    })
    .run()
  return placeholderId
}

interface FoundPlaceholder {
  pos: number
  node: ProseMirrorNode
}

function findPlaceholder(
  doc: ProseMirrorNode,
  typeName: WikiEmbedPlaceholderType,
  placeholderId: string,
): FoundPlaceholder | null {
  let found: FoundPlaceholder | null = null
  doc.descendants((node, pos) => {
    if (found) return false
    if (node.type.name === typeName && node.attrs.uploadingPlaceholderId === placeholderId) {
      found = { pos, node }
      return false
    }
    return true
  })
  return found
}

/**
 * Swap a placeholder for its resolved upload attrs. Always clears
 * `uploadingPlaceholderId` on top of the caller's `attrs`. Returns
 * whether a matching placeholder was found.
 */
export function resolveWikiEmbedPlaceholder(
  editor: Editor | null,
  typeName: WikiEmbedPlaceholderType,
  placeholderId: string,
  attrs: Record<string, unknown>,
): boolean {
  if (!editor || editor.isDestroyed) return false
  let resolved = false
  editor
    .chain()
    .command(({ tr, state, dispatch }) => {
      const found = findPlaceholder(state.doc, typeName, placeholderId)
      if (!found) return false
      if (dispatch) {
        tr.setNodeMarkup(found.pos, undefined, {
          ...found.node.attrs,
          ...attrs,
          uploadingPlaceholderId: null,
        })
        // Resolving an async upload must not be an undo step — undo
        // right after a resolve would restore a dead skeleton.
        tr.setMeta('addToHistory', false)
      }
      resolved = true
      return true
    })
    .run()
  return resolved
}

/**
 * Flip a placeholder into (or out of) its FAILED state — the NodeViews
 * render an error branch with Retry / Dismiss instead of the spinner.
 * The node stays in the doc so a failed upload is visible and
 * recoverable (a silently-removed skeleton reads as "the drop did
 * nothing"); the flush-time strip still removes it from every
 * persistence path via `uploadingPlaceholderId`. Same
 * `addToHistory: false` rationale as resolve.
 */
export function setWikiEmbedPlaceholderFailed(
  editor: Editor | null,
  typeName: WikiEmbedPlaceholderType,
  placeholderId: string,
  failed: boolean,
): boolean {
  if (!editor || editor.isDestroyed) return false
  let marked = false
  editor
    .chain()
    .command(({ tr, state, dispatch }) => {
      const found = findPlaceholder(state.doc, typeName, placeholderId)
      if (!found) return false
      if (dispatch) {
        tr.setNodeMarkup(found.pos, undefined, { ...found.node.attrs, uploadingFailed: failed })
        tr.setMeta('addToHistory', false)
      }
      marked = true
      return true
    })
    .run()
  return marked
}

/**
 * Remove a placeholder whose upload failed / was canceled. An inline
 * chip that is its paragraph's only child takes the (now-empty)
 * wrapping paragraph with it, so a failed drop doesn't leave a stray
 * blank line.
 */
export function failWikiEmbedPlaceholder(
  editor: Editor | null,
  typeName: WikiEmbedPlaceholderType,
  placeholderId: string,
): void {
  if (!editor || editor.isDestroyed) return
  editor
    .chain()
    .command(({ tr, state, dispatch }) => {
      const found = findPlaceholder(state.doc, typeName, placeholderId)
      if (!found) return false
      if (dispatch) {
        const $pos = state.doc.resolve(found.pos)
        const parent = $pos.parent
        if (found.node.isInline && parent.type.name === 'paragraph' && parent.childCount === 1) {
          const parentPos = $pos.before()
          tr.delete(parentPos, parentPos + parent.nodeSize)
        } else {
          tr.delete(found.pos, found.pos + found.node.nodeSize)
        }
        // Same rationale as resolve — a failure cleanup isn't a user
        // edit; redo must not resurrect the skeleton.
        tr.setMeta('addToHistory', false)
      }
      return true
    })
    .run()
}
